








import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {Chart} from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import Highcharts from 'highcharts';
import { colors } from '../config';
import {
    applyPolarCompassSizing,
    fitPolarCompassChartSize,
} from '@/utils/chartPresentationSizing';

More(Highcharts);

@Component({components: {Chart}})
export default class Compass extends Vue {
    @Prop() readonly rankings!: any;
    @Prop() readonly years!: number;
    @Prop() readonly showBransch!: boolean;
    @Prop({default: true}) readonly showLabels!: boolean;
    @Prop() dark!: boolean;
    @Prop() containerWidth: any;
    @Prop() currentLanguage: any;

    chartOptions: any = {};

    @Watch('rankings', { immediate: true, deep: true })
    onRankingsChanged() {
        this.update();
    }
    @Watch('containerWidth', { immediate: false, deep: true })
    containerWidthChanged() {
        if (!this.chartOptions || !this.chartOptions.chart) {
            this.update();
            return;
        }
        applyPolarCompassSizing(this.chartOptions, this.containerWidth);
        this.reflowChart();
    }

    update() {
        this.chartOptions = {
            chart: {
                polar: true,
                type: 'area',
                backgroundColor: 'transparent',
            },

            credits: {
                enabled: false,
            },

            title: null,

            pane: {},

            xAxis: {
                // tslint:disable-next-line:max-line-length
                categories: this.dark ? [
                    '<span style="font-weight: 600; color: white;">' +
                        (this.currentLanguage === 'en' ? 'Return' : Vue.prototype.$t('Avkastning')) + '</span>',
                    (this.currentLanguage === 'en' ? 'Operating margin' : Vue.prototype.$t('Rörelsemarginal')),
                    (this.currentLanguage === 'en' ? 'Growth' : Vue.prototype.$t('Tillväxt')),
                    '<span  style="font-weight: 600; color: white;">' +
                        (this.currentLanguage === 'en' ? 'Future outlook' : Vue.prototype.$t('Framtidsutsikter')) + '</span>',
                    (this.currentLanguage === 'en' ? 'Solidity' : Vue.prototype.$t('Soliditet')),
                    (this.currentLanguage === 'en' ? 'Working capital in % of Net sales' : Vue.prototype.$t('Rörelsekapital i % av omsättning')),
                    '<span style="font-weight: 600; color: white;">' +
                        (this.currentLanguage === 'en' ? 'Financial strength' : Vue.prototype.$t('Finansiell styrka')) + '</span>',
                    (this.currentLanguage === 'en' ? 'Quick ratio' : Vue.prototype.$t('Kassalikviditet')),
                    // tslint:disable-next-line:max-line-length
                    (this.currentLanguage === 'en' ? 'Interest coverage ratio' : Vue.prototype.$t('Räntetäckningsgrad')),
                    '<span style="font-weight: 600; color: white;">' +
                        (this.currentLanguage === 'en' ? 'Short term readiness' : Vue.prototype.$t('Kortsiktig beredskap')) + '</span>',
                    (this.currentLanguage === 'en' ? 'Risk buffer' : Vue.prototype.$t('Riskbuffert')),
                    (this.currentLanguage === 'en' ? 'Return on equity' : Vue.prototype.$t('Räntabilitet på eget kapital')),
                ] : [
                    '<span style="font-weight: 600; color: black;">' +
                        Vue.prototype.$t('Avkastning') + '</span>',
                    Vue.prototype.$t('Rörelsemarginal'),
                    Vue.prototype.$t('Tillväxt'),
                    '<span  style="font-weight: 600; color: black;">' +
                        Vue.prototype.$t('Framtidsutsikter') + '</span>',
                    Vue.prototype.$t('Soliditet'),
                    Vue.prototype.$t('Rörelsekapital i % av omsättning'),
                    '<span style="font-weight: 600; color: black;">' +
                        Vue.prototype.$t('Finansiell styrka') + '</span>',
                    Vue.prototype.$t('Kassalikviditet'),
                    Vue.prototype.$t('Räntetäckningsgrad'),
                    '<span style="font-weight: 600; color: black;">' +
                        Vue.prototype.$t('Kortsiktig beredskap') + '</span>',
                    Vue.prototype.$t('Riskbuffert'),
                    Vue.prototype.$t('Räntabilitet på eget kapital'),
                ],
                tickmarkPlacement: 'on',
                lineWidth: 0,
                gridLineColor: this.dark ? colors.Black : colors.DarkGrey,
                plotLines: [
                    {
                        color: colors.Black,
                        width: this.dark ? this.transformPx(3) : 2,
                        zIndex: 1,
                        value: 0,
                    },
                    {
                        color: colors.Black,
                        width: this.dark ? this.transformPx(3) : 2,
                        zIndex: 1,
                        value: 3,
                    },
                    {
                        color: colors.Black,
                        width: this.dark ? this.transformPx(3) : 2,
                        zIndex: 1,
                        value: 6,
                    },
                    {
                        color: colors.Black,
                        width: this.dark ? this.transformPx(3) : 2,
                        zIndex: 1,
                        value: 9,
                    },
                ],
                labels: {
                    enabled: true,
                    style: {
                        fontSize: '1cqw',
                        color: this.dark ? colors.LightGrey : colors.Dark,
                    },
                    distance: this.transformPx(25),
                },
            },

            yAxis: {
                gridLineInterpolation: 'polygon',
                gridLineDashStyle: 'longdash',
                gridLineColor: this.dark ? colors.Black : colors.LightGrey,
                lineWidth: 0,
                min: 0,
                max: 100,
                tickInterval: 10,
                labels: {
                    enabled: false,
                },
            },

            tooltip: {
                shared: true,
                useHTML: true,
                headerFormat: '<table>',
                pointFormat: '<tr><td style="font-size: 1.25cqw;">{series.name}</td><td style="text-align: right; font-size: 1.25cqw; padding-left: 10px"><b>{point.y}</b></td></tr>',
                backgroundColor: this.dark ? colors.Dark : colors.LightGrey,
                style: {
                    color: this.dark ? colors.LightGrey : colors.Black,
                },
            },

            legend: {
                reversed: true,
                enabled: true,
                floating: true,
                borderWidth: this.transformPx(1),
                borderColor: this.dark ? colors.Black : colors.DarkGrey,
                padding: this.transformPx(16),
                backgroundColor: this.dark ? colors.Dark : colors.Light,
                align: 'left',
                verticalAlign: 'top',
                x: 0,
                y: 0,
                layout: 'vertical',
                itemStyle: {
                    font: '1cqw Assistant, Verdana, sans-serif',
                    color: this.dark ? colors.LightGrey : colors.Black,
                },
                itemHoverStyle: {
                    color: this.dark ? colors.Light : colors.DarkGrey,
                },
                symbolHeight: this.transformPx(16),
                symbolWidth: this.transformPx(16),
            },

            plotOptions: {
                series: {
                    animation: false,
                    fillOpacity: 0.5,
                    marker: {
                        radius: 0,
                        states: {
                            hover: {
                                radius: this.transformPx(5),
                            },
                        },
                        symbol: 'circle',
                    },
                    lineWidth: this.transformPx(1),
                    pointPlacement: 'on',
                },
            },

            navigation: {
                buttonOptions: {
                    enabled: false,
                },
            },

            series: this.getSeries(),
        };
        applyPolarCompassSizing(this.chartOptions, this.containerWidth);
        this.reflowChart();
    }

    mounted() {
        this.reflowChart();
    }

    reflowChart() {
        this.$nextTick(() => {
            const slot = this.$refs.chartSlot as HTMLElement;
            fitPolarCompassChartSize(
                this.chartOptions,
                slot,
                this.containerWidth,
            );
            const chart = this.$refs.chart as any;
            if (chart && chart.chart) {
                chart.chart.reflow();
            }
        });
    }

    getSeries() {
      if (this.rankings === undefined) {
        return [];
      }
      if (this.rankings.sheets === undefined) {
        return [];
      }

      // Lägg till grundstruktur-serie som alltid visas
      const baseStructure = {
        name: 'Grundstruktur',
        showInLegend: false,
        enableMouseTracking: false,
        data: Array(12).fill(0),
        color: 'transparent',
        zIndex: 0,
      };
      const series = [];
      series.push(baseStructure);
      for (let i = 0; i < this.rankings.count; i++) {
        if (i === this.years) {
          break;
        }
        const sheet = this.rankings.sheets[i];
        series.unshift({
          name: `${sheet.DATE_TO.substring(0, 7)} (${sheet.MONTHS} ${(this.currentLanguage === 'en' ? 'mo' : Vue.prototype.$t('mån'))})`,
          data: this.rankingValues(i),
          // tslint:disable-next-line:max-line-length
          color: this.dark ? [colors.Secondary, colors.Tertiary, colors.Red][i] : [colors.Primary, colors.Secondary, colors.Tertiary][i],
          // tslint:disable-next-line:max-line-length
          lineColor: this.dark ? [colors.Secondary, colors.Tertiary, colors.Red][i] : [colors.Primary, colors.Secondary, colors.Tertiary][i],
          lineWidth: this.dark ? this.transformPx(2) : 1,
          visible: [true, false, false][i],
          showInLegend: true,
          enableMouseTracking: true,
        });
      }
      const br = this.rankings.sheets[0].industryRanks;
      if (this.showBransch && br.score !== 0) {
        series.unshift({
          name: this.currentLanguage === 'en' ? 'Industry median' : Vue.prototype.$t('Branschmedian'),
          data: [
              br.yield, br.operatingMargin, br.growth, br.longTerm,
              br.solidity, br.workingCapitalPct, br.financialStrength, br.quickRatio,
              br.interestCoverage, br.shortTerm, br.riskBuffer, br.returnOnCapital,
          ],
          color: this.dark ? colors.Tertiary : colors.DarkGrey,
          lineColor: this.dark ? colors.Tertiary : colors.DarkGrey,
          lineWidth: this.dark ? this.transformPx(3) : 1,
          fillColor: 'rgba(0, 0, 0, 0.0)',
          dashStyle: 'longDash',
          visible: true,
          showInLegend: true,
          enableMouseTracking: true,
        });
      }
      return series;
    }

    rankingValues(i: number) {
        if (!this.rankings.sheets) {
        return [];
        }
        const r = this.rankings.sheets[i].ranks;
        return [
            this.withLabel(r.yield),
            r.operatingMargin,
            r.growth,
            this.withLabel(r.longTerm),
            r.solidity,
            r.workingCapitalPct,
            this.withLabel(r.financialStrength),
            r.quickRatio,
            r.interestCoverage,
            this.withLabel(r.shortTerm),
            r.riskBuffer,
            r.returnOnCapital,
        ];
    }

    withLabel(v: any) {
      if (!this.showLabels) {
        return v;
      }
      return {
          dataLabels: {
              enabled: true,
              align: 'center',
              backgroundColor: this.dark ? colors.Secondary : colors.Primary,
              color: colors.Light,
              allowOverlap: true,
              style: {
              textOutline: false,
              fontSize: '1cqw',
              fontWeight: 'bold',
              },
              verticalAlign: 'middle',
          },
          y: v,
      };
    }

    transformPx(value: any) {
        const width =
            this.containerWidth > 0 ? this.containerWidth : 1920;
        if (value * (width / 1920) < 1) {
            return 1;
        }
        return value * (width / 1920);
    }
}


